import { useState } from "react";
import { createPortal } from "react-dom";

import "./change-deal-date-modal.scss";

/**
 * "Change Deal Date" picker. Moves a done / fully-paid deal's creation date —
 * which is what decides the Kanban month, the Transactions P&L month, the
 * reconciliation period AND the Date on the deal's Xero invoice / purchase
 * order. Shared by the Kanban board and the Orders list, exactly like
 * ChangeSaleLeadModal.
 *
 * `onConfirm(date)` should PUT /requests/:id/deal-date and RESOLVE with the
 * response body. Two distinct outcomes are surfaced differently:
 *
 *   - it throws           → nothing moved; show an error and stay open
 *   - it resolves with a non-empty `xero.failures` → RC moved but at least one
 *     accounting document did not. The modal stays open showing which, because
 *     closing on that would let a half-applied change read as done.
 *
 * `currentDate` prefills the input (ISO yyyy-mm-dd) so the rep sees what they
 * are changing FROM.
 */
const toInputDate = (value) => {
  if (!value) return "";
  // Accepts "2026-08-12", "2026-08-12 14:03:00" and full ISO — all we want is
  // the calendar day, and slicing avoids `new Date()` shifting the day across
  // the timezone boundary for a midnight timestamp.
  const match = String(value).match(/^(\d{4}-\d{2}-\d{2})/);
  return match ? match[1] : "";
};

const ChangeDealDateModal = ({ onClose, onConfirm, currentDate }) => {
  const original = toInputDate(currentDate);
  const [date, setDate] = useState(original);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [xeroFailures, setXeroFailures] = useState([]);

  const confirm = async () => {
    if (!date) return;
    setSaving(true);
    setError(null);
    setXeroFailures([]);
    try {
      const result = await onConfirm(date);
      const failures = result?.xero?.failures || [];
      if (failures.length) {
        setXeroFailures(failures);
        setSaving(false);
        return;
      }
      onClose();
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          "Couldn’t change the deal date. Please try again.",
      );
      setSaving(false);
    }
  };

  return createPortal(
    <div className="cddm__overlay" onMouseDown={onClose}>
      <div
        className="cddm"
        role="dialog"
        aria-modal="true"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="cddm__header">
          <h3>Change Deal Date</h3>
          <button
            type="button"
            className="cddm__close"
            aria-label="Close"
            onClick={onClose}
          >
            ×
          </button>
        </div>
        <div className="cddm__body">
          <p className="cddm__hint">
            Moves this whole deal (every order &amp; PX-sell in the group) to the
            chosen date, so it lands in the right Kanban and P&amp;L month. The
            date on its Xero invoice and purchase order is updated to match.
          </p>
          <label className="cddm__label" htmlFor="cddm-date">
            Deal date
          </label>
          <input
            id="cddm-date"
            type="date"
            className="cddm__input"
            value={date}
            disabled={saving}
            onChange={(e) => setDate(e.target.value)}
          />
          {original && (
            <div className="cddm__current">Currently {original}</div>
          )}
          {error && <div className="cddm__error">{error}</div>}
          {!!xeroFailures.length && (
            <div className="cddm__warning">
              <strong>The date was changed in RC, but not in Xero:</strong>
              <ul>
                {xeroFailures.map((f, i) => (
                  <li key={i}>
                    {f.document}: {f.error}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
        <div className="cddm__footer">
          <button
            type="button"
            className="cddm__btn"
            onClick={onClose}
            disabled={saving}
          >
            {xeroFailures.length ? "Close" : "Cancel"}
          </button>
          <button
            type="button"
            className="cddm__btn cddm__btn--primary"
            onClick={confirm}
            disabled={!date || saving || date === original}
          >
            {saving ? "Saving…" : "Change Date"}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default ChangeDealDateModal;
